import { Alert, Button, Card, Space } from 'antd'
import DeleteAll from './DeleteAll'
import { default as UpdateCatalogModal } from '../../Catalog/Modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faFileImport, faFileExport, faBoxArchive } from '@fortawesome/pro-solid-svg-icons'
import { green } from '@ant-design/colors'
import ThemableButton from '../../Items/ThemableButton'
import { useContext, useRef, useState } from 'react'
import { UploadResult, uploadCsv } from './importExportApi'
import { buildImportEventMetadata } from './importExportModel'
import FileDownload from './FileDownload'
import FileUpload from './FileUpload'
import BulkRetireDrawer from './BulkRetireDrawer'
import { AuthenticationContext } from '../../../AuthenticationContext'
import './ImportExport.css'
import dayjs from 'dayjs'

type Props = {
  createdUtc?: string
}

const ImportExport = ({createdUtc}: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const isSuperUser = currentUser?.isSuperUser ?? false
  const tenantCreatedUtc = createdUtc ?? currentUser?.tenant.createdUtc
  const fileDownloadRef = useRef<any>()
  const customerUploadRef = useRef<any>()
  const itemUploadRef = useRef<any>()
  const [uploadResult, setUploadResult] = useState<UploadResult | undefined>()
  const [isBulkRetireOpen, setIsBulkRetireOpen] = useState(false)

  const [areCustomersExporting, setAreCustomersExporting] = useState(false)
  const exportCustomers = async () => {
    setAreCustomersExporting(true)
    await fileDownloadRef.current.download('export/customers', 'Customers.csv')
    setAreCustomersExporting(false)
  }

  const [areItemsExporting, setAreItemsExporting] = useState(false)
  const exportItems = async () => {
    setAreItemsExporting(true)
    await fileDownloadRef.current.download('export/items', 'Items.csv')
    setAreItemsExporting(false)
  }

  const onCustomerFileSelected = async (file: File) => {
    const response = await uploadCsv(file, '/import/customers')
    setUploadResult(response)
    trackImport(response, 'customers')
  }

  const onItemFileSelected = async (file: File) => {
    const response = await uploadCsv(file, '/import/items')
    setUploadResult(response)
    trackImport(response, 'items')
  }

  const trackImport = (data: UploadResult, type: string) => {
    window.Intercom?.('trackEvent', `imported-${type}`, buildImportEventMetadata(data))
  }

  const showFreeLink = (createdDate?: string): boolean => {
    if(!createdDate) {
      return false
    }

    const daysSinceCreated = dayjs().diff(dayjs(createdDate), 'days')

    return daysSinceCreated < 60 
  }

  return <Space direction="vertical" size="large" className="import-export">
    <h3>Customers</h3>
    <Space direction="vertical" size="middle">
      <Space>
        <ThemableButton
          colorPrimary={green.primary!}
          icon={<FontAwesomeIcon icon={faDownload} />}
          size="large"
          type="primary"
          onClick={() => fileDownloadRef.current.download('import/customers', 'customers.csv')}
        >
          Download CSV Template
        </ThemableButton>
        <Button
          icon={<FontAwesomeIcon icon={faFileImport} />}
          size="large"
          type="primary"
          onClick={() => customerUploadRef.current.selectFile()}
        >
          Import from CSV
        </Button>
        <Button
          icon={<FontAwesomeIcon icon={faFileExport} />}
          size="large"
          type="primary"
          onClick={exportCustomers}
          loading={areCustomersExporting}
        >
          Export to CSV
        </Button>
        {isSuperUser && <DeleteAll type="customers" url="/web/customers" /> }
      </Space>
      <Alert
        description={<>
          For <strong>Country</strong> and <strong>State</strong> <em>(where applicable)</em> fields use ISO-3166 two-digit country codes (e.g. United States = US, Canada = CA, New York = NY, Ontario = ON, etc.)
        </>}
        style={{ width: 'initial' }}
      />
    </Space>
    <h3>Items</h3>
    <Space>
      <ThemableButton
        colorPrimary={green.primary!}
        icon={<FontAwesomeIcon icon={faDownload} />}
        size="large"
        type="primary"
        onClick={() => fileDownloadRef.current.download('import/items', 'items.csv')}
        loading={areItemsExporting}
      >
        Download CSV Template
      </ThemableButton>
      <Button
        icon={<FontAwesomeIcon icon={faFileImport} />}
        size="large"
        type="primary"
        onClick={() => itemUploadRef.current.selectFile()}
      >
        Import from CSV
      </Button>
      <Button
        icon={<FontAwesomeIcon icon={faFileExport} />}
        size="large"
        type="primary"
        onClick={exportItems}
      >
        Export to CSV
      </Button>
      <UpdateCatalogModal />
      {isSuperUser && <DeleteAll type="items" url="/web/catalog-items/retire-all" /> }
      {isSuperUser && <Button
        icon={<FontAwesomeIcon icon={faBoxArchive} />}
        size="large"
        onClick={() => setIsBulkRetireOpen(true)}
      >
        Bulk Retire By Selection
      </Button>}
    </Space>
    {uploadResult && <>
      {uploadResult.success
        ? <Alert
          message="Success!"
          showIcon
          description={`You have imported ${uploadResult.total} records`}
          type="success"
        />
        : <Alert
          message="Import Failed"
          showIcon
          description={`${uploadResult.total} records were imported. There were ${uploadResult.errors.length} errors`}
          type="error"
        />
      }
      {uploadResult.errors.length &&
        <ul>
          {uploadResult.errors.map((error, index) => {
            return <li key={index}>
              <small>{error.propertyName}</small>
              <strong>{error.value}</strong>
              {error.message}
            </li>
          })}
        </ul>
      }
      {uploadResult.duplicates.length > 0 && <>
        The following duplicate records where ignored:
        <ul>
          {uploadResult.duplicates.map((duplicate, index) => {
            return <li key={index}>
              <small>{duplicate.propertyName}</small>
              <strong>{duplicate.value}</strong>
              {duplicate.message}
            </li>
          })}
        </ul>
      </>}
    </>}
    <FileDownload ref={fileDownloadRef} />
    <FileUpload ref={customerUploadRef} onFileSelected={onCustomerFileSelected} />
    <FileUpload ref={itemUploadRef} onFileSelected={onItemFileSelected} />
    {isSuperUser && <BulkRetireDrawer open={isBulkRetireOpen} onClose={() => setIsBulkRetireOpen(false)} />}
    <Card title={'Need Assistance?'}>
      <span>We are happy to help you get your lists uploaded or updated should you need help. Please use this link to upload your customer list or item list.  (Please note that additional fees may apply.)</span>
      <br />
      <br />
      <div>
        <ThemableButton
            type="link"
            href={showFreeLink(tenantCreatedUtc) ? "https://synkedup.com/complimentary-bulk-data-import/" : 'https://synkedup.com/bulk-data-import/'}
        >
          Bulk data import
        </ThemableButton> 
      </div>
    </Card>
  </Space>
}

export default ImportExport
